/**
 * @file EvidenceCard.tsx
 *
 * @description This component displays details of a video evidence file and allows officers to process,
 * view, or select videos for report generation. Part of the police evidence management system.
 *
 * @module Components
 * @requires React
 * @requires Material-UI
 * @requires js-cookie
 * @requires react-router-dom
 */

import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  Tooltip,
  AlertTitle,
  CardMedia,
  Chip,
  Stack,
  Grid,
  alpha,
  useTheme,
} from "@mui/material";
import { VideoPreview } from "../../shared/interfaces/VideoPreview";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import {
  PlayArrow,
  Downloading as Download,
  CloudDownload,
  VideoLibrary,
  CalendarToday,
  AccessTime,
  Videocam,
  Description,
  Label,
  FilterAltOff,
} from "@mui/icons-material";
import { useToast } from "../../shared/toast";
import { VideoStatus } from "../../shared/interfaces/VideoData";
import { formatDuration } from "../../shared/utils/formatDuration";
import { bodycamClient } from "../../api/clients";
import audioThumbnailLight from "../../assets/audio_thumbnail_light.svg";
import audioThumbnailDark from "../../assets/audio_thumbnail_dark.svg";
import { MAX_VIDEOS_PER_REPORT } from "./hooks/useVideoSelection";
/**
 * Props for the EvidenceCard component
 * @interface EvidenceCardProps
 * @property {VideoPreview} video - The video evidence data to display
 * @property {boolean} [isSelected=false] - Whether this card is currently selected
 * @property {Function} [onSelect] - Callback when the card is selected/deselected
 * @property {number} numberSelected - How many cards are currently selected in the parent component
 */
interface EvidenceCardProps {
  video: VideoPreview;
  isSelected?: boolean;
  onSelect?: (videoId: string, isSelected: boolean) => void;
  numberSelected: number;
  /** Called after an AI-filtered video's fetch is successfully requested, so
   *  the parent can optimistically flip this video to FETCHING and keep the
   *  card visible while the backend processes the request. */
  onFetchRequested?: (videoId: string) => void;
}

/**
 * @name EvidenceCard
 *
 * @description Card component that displays a single piece of video evidence with its metadata
 * and provides controls to process, view, or select it for batch operations.
 *
 * The card has different visual states based on selection status and video fetching status.
 * It enforces a maximum of 5 videos can be selected at once through the parent component's counter.
 *
 * @param {EvidenceCardProps} props - Component props
 *
 * @returns {React.ReactElement} Rendered evidence card
 */
export const EvidenceCard: React.FC<EvidenceCardProps> = ({
  video,
  isSelected = false,
  onSelect,
  numberSelected,
  onFetchRequested,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const toast = useToast();
  const [thumbnailError, setThumbnailError] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string>("");
  const isAudio = video.fileType?.startsWith("audio") ?? false;

  // AI-filtered (NOT_EVIDENCE): never fetched, so it can only be fetched, not
  // viewed/selected. `fetchRequested` tracks the local optimistic state after a
  // user requests ingestion (the real status flips via the list's poll).
  const isFiltered = video.status === VideoStatus.NOT_EVIDENCE;
  const [fetchRequested, setFetchRequested] = useState(false);
  const [isRequestingFetch, setIsRequestingFetch] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;

    // Skip thumbnail fetch for videos still processing or AI-filtered — no
    // thumbnail exists yet (the file was never fetched).
    if (
      video.status === VideoStatus.FETCHING ||
      video.status === VideoStatus.COMPRESSING ||
      video.status === VideoStatus.NOT_EVIDENCE
    ) {
      return;
    }

    // Audio evidence uses a preset waveform thumbnail; no backend frame to fetch
    if (isAudio) {
      return;
    }

    const fetchThumbnail = async () => {
      try {
        setThumbnailError(false);
        const response = await bodycamClient.get("/thumbnail", {
          params: { video_id: video.id },
        });

        setThumbnailUrl(response.data.url);
      } catch (error) {
        console.error("Failed to fetch thumbnail:", error);
        setThumbnailError(true);
      }
    };

    fetchThumbnail();

    // Cleanup: revoke the object URL when component unmounts or video changes
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [video.id, video.status, isAudio]);

  // Whether the video is ready to be viewed or processed
  const isVideoReady = [
    VideoStatus.READY,
    VideoStatus.TRANSCRIPT_READY,
    VideoStatus.TRANSCRIBING,
  ].includes(video.status);

  // Whether the video is currently being processed by the system
  const isFetching = video.status === VideoStatus.FETCHING;
  const isCompressing = video.status === VideoStatus.COMPRESSING;
  /**
   * Whether the card can be selected based on status and selection limits
   * A card is selectable if it's ready and either already selected or
   * there are fewer than 5 selections total
   */
  const isSelectable = isVideoReady && (numberSelected < MAX_VIDEOS_PER_REPORT || isSelected);

  // Determine the appropriate color to display based on video fetching status
  const getStatusColor = () => {
    switch (video.status) {
      case VideoStatus.TRANSCRIPT_READY:
      case VideoStatus.READY:
        return "success";
      case VideoStatus.COMPRESSING:
      case VideoStatus.FETCHING:
      case VideoStatus.TRANSCRIBING:
        return "warning";
      case VideoStatus.NOT_EVIDENCE:
        return "default";
      default:
        return "error";
    }
  };

  // Friendlier label than the raw enum for the AI-filtered case.
  const statusLabel = isFiltered ? "AI-Filtered" : video.status;

  /**
   * @name getAutoThumbnail
   *
   * @description Returns correct thumbnail URL based on whether the evidence is audio or video.
   *  Audio files use a preset thumbnail image, while video files use the fetched thumbnail URL.
   *
   * @returns {string} URL of the appropriate thumbnail image
   */
  const getAutoThumbnail = () => {
    if (isAudio) {
      return theme.palette.mode === "dark"
        ? audioThumbnailDark
        : audioThumbnailLight;
    }
    return thumbnailUrl;
  }

  /**
   * @name formatRecordedDateTime
   *
   * @description Formats a recorded date string into a user-friendly date and time
   *
   * @param {string} dateString - ISO date string representing when the video was recorded
   *
   * @returns {string} Formatted date and time or the original string if parsing fails
   */
  const formatRecordedDateTime = (dateString: string) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;

    const formattedDate = date.toLocaleDateString(undefined);
    const formattedTime = date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });

    return `${formattedDate} at ${formattedTime.toLowerCase()}`;
  };

  // Handles clicks on the main action button based on video status
  const handleButtonClick = () => {
    if (isVideoReady) {
      navigate(`/generate-report/${video.id}`);
      return;
    } else if (video.status === VideoStatus.FETCHING) {
      toast.error(
        "This video is currently fetching. Please wait for it to finish.",
      );
      return;
    }
  };

  // Request ingestion of an AI-filtered video. Triggers the normal fetch
  // pipeline server-side; the parent flips this card to FETCHING immediately
  // via onFetchRequested, and the list poll reconciles the real status.
  const handleFetchFiltered = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isRequestingFetch || fetchRequested) return;
    setIsRequestingFetch(true);
    try {
      await bodycamClient.post("/evidence/fetch", {
        evidenceId: video.id,
        vendor: video.device,
      });
      setFetchRequested(true);
      toast.success(
        "Fetch requested. This video will download and process shortly.",
      );
      onFetchRequested?.(video.id);
    } catch (error) {
      console.error("Failed to request fetch:", error);
      toast.error(
        "Failed to request this video. Please try again in a moment.",
      );
    } finally {
      setIsRequestingFetch(false);
    }
  };

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
      {/* Main evidence card */}
      <Card
        sx={{
          height: "100%",
          width: "100%",
          minWidth: { xs: "200px", xxl: "240px", uhd: "280px" },
          maxWidth: { xs: "300px", xxl: "360px", uhd: "420px" },
          margin: "auto",
          borderRadius: "12px",
          backgroundColor: isSelected
            ? alpha(theme.palette.primary.main, 0.12)
            : numberSelected > 0 && !isSelectable
              ? "action.disabledBackground"
              : "background.paper",
          transition: "all 0.3s ease",
          border: isSelected ? `2px solid ${theme.palette.primary.main}` : 1,
          borderColor: isSelected ? "primary.main" : "divider",
          "&:hover": {
            transform: isSelectable ? "translateY(-4px)" : "none",
            boxShadow: isSelectable ? "0 4px 20px rgba(0, 0, 0, 0.15)" : "none",
            transition: "all 0.3s ease",
          },
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          cursor: isSelectable ? "pointer" : "default",
          opacity: numberSelected > 0 && !isSelectable ? 0.7 : 1,
          position: "relative",
        }}
        onClick={() => {
          if (isSelectable && onSelect) {
            onSelect(video.id, !isSelected);
          }
        }}
      >
        <Box sx={{ position: "relative" }}>
          {isFiltered ? (
            <Box
              sx={{
                height: { xs: 140, xxl: 170, uhd: 200 },
                bgcolor: "action.hover",
                borderRadius: "12px 12px 0 0",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                color: "text.secondary",
                px: 2,
                textAlign: "center",
              }}
            >
              <FilterAltOff sx={{ fontSize: 36 }} />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Filtered out by AI screening
              </Typography>
              <Typography variant="caption">
                Not yet downloaded — fetch to view
              </Typography>
            </Box>
          ) : isFetching || isCompressing ? (
            <Alert
              severity="info"
              icon={false}
              sx={{
                height: { xs: 140, xxl: 170, uhd: 200 },
                borderRadius: "12px 12px 0 0",
                alignItems: "center",
                "& .MuiAlertTitle-root": {
                  fontWeight: "bold",
                  textAlign: "center",
                  width: "100%",
                  fontSize: { xs: "0.9rem", xxl: "1rem", uhd: "1.1rem" },
                },
                "& .MuiAlert-message": {
                  textAlign: "center",
                  width: "100%",
                  fontSize: { xs: "0.75rem", xxl: "0.85rem", uhd: "0.95rem" },
                },
              }}
            >
              <AlertTitle>Processing</AlertTitle>
              Please wait...
            </Alert>
          ) : !thumbnailError && (thumbnailUrl || isAudio) ? (
            <CardMedia
              component="img"
              image={getAutoThumbnail()}
              alt={video.fileName}
              sx={{
                objectFit: "cover",
                height: { xs: 140, xxl: 170, uhd: 200 },
              }}
              onError={() => setThumbnailError(true)}
            />
          ) : (
            <Box
              sx={{
                height: { xs: 140, xxl: 170, uhd: 200 },
                bgcolor: "action.hover",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
              }}
            >
              <VideoLibrary sx={{ fontSize: 40 }} />
            </Box>
          )}
          <Chip
            label={statusLabel}
            color={getStatusColor()}
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              fontWeight: "bold",
              boxShadow: 1,
            }}
          />
        </Box>

        <CardContent sx={{ p: 2, flexGrow: 1 }}>
          <Stack spacing={1.5} mt={1}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Description
                fontSize="small"
                sx={{ color: "text.secondary", fontSize: "1rem" }}
              />
              <Tooltip title={video.fileName} placement="top">
                <Typography
                  variant="body2"
                  color="text.primary"
                  fontWeight="bold"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {video.fileName}
                </Typography>
              </Tooltip>
            </Box>

            {video.label && (
              <Box display="flex" alignItems="center" gap={1.5}>
                <Label
                  fontSize="small"
                  sx={{ color: "text.secondary", fontSize: "1rem" }}
                />
                <Tooltip title={video.label} placement="top">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {video.label}
                  </Typography>
                </Tooltip>
              </Box>
            )}

            <Box display="flex" alignItems="center" gap={1.5}>
              <CalendarToday
                fontSize="small"
                sx={{ color: "text.secondary", fontSize: "1rem" }}
              />
              <Typography variant="body2" color="text.primary">
                {formatRecordedDateTime(video.recordedDate)}
              </Typography>
            </Box>

            {/* Duration is unknown for AI-filtered videos (never fetched). */}
            {!isFiltered && (
              <Box display="flex" alignItems="center" gap={1.5}>
                <AccessTime
                  fontSize="small"
                  sx={{ color: "text.secondary", fontSize: "1rem" }}
                />
                <Typography variant="body2" color="text.primary">
                  {formatDuration(video.duration)}
                </Typography>
              </Box>
            )}

            <Box display="flex" alignItems="center" gap={1.5}>
              <Videocam
                fontSize="small"
                sx={{ color: "text.secondary", fontSize: "1rem" }}
              />
              <Typography
                variant="body2"
                color="text.primary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {video.device || "Manual Upload"}
              </Typography>
            </Box>

            {/* Why the AI filtered this video out. */}
            {isFiltered && video.judgeReasons && video.judgeReasons.length > 0 && (
              <Tooltip title={video.judgeReasons.join("; ")} placement="top">
                <Box display="flex" alignItems="flex-start" gap={1.5}>
                  <FilterAltOff
                    fontSize="small"
                    sx={{ color: "text.secondary", fontSize: "1rem", mt: 0.25 }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {video.judgeReasons.join("; ")}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Stack>
        </CardContent>

        {/* Action button section */}
        <Box sx={{ p: 2, pt: 0 }}>
          <Button
            loading={isFiltered ? isRequestingFetch : isFetching || isCompressing}
            disabled={isFiltered ? isRequestingFetch || fetchRequested : isFetching || isCompressing}
            variant="contained"
            onClick={isFiltered ? handleFetchFiltered : handleButtonClick}
            startIcon={
              isFiltered ? (
                <CloudDownload />
              ) : video.status === VideoStatus.READY ||
                video.status === VideoStatus.TRANSCRIPT_READY ? (
                <PlayArrow />
              ) : (
                <Download />
              )
            }
            sx={{
              width: "100%",
              textTransform: "none",
              color: "#fff",
              background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 50%)`,
              boxShadow: `0 4px 14px 0 ${alpha(
                theme.palette.primary.main,
                0.3,
              )}`,
              "&:hover": {
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.light,
                  0.9,
                )} 0%, ${theme.palette.primary.light} 50%)`,
                boxShadow: `0 6px 20px 0 ${alpha(
                  theme.palette.primary.main,
                  0.4,
                )}`,
              },
              "&.MuiButton-loading": {
                background: `${theme.palette.primary.dark} !important`,
                backgroundColor: `${theme.palette.primary.dark} !important`,
              },
              "&&.Mui-disabled": {
                background: `${theme.palette.primary.dark} !important`,
                backgroundColor: `${theme.palette.primary.dark} !important`,
              },
              "& .MuiButton-loadingIndicator": {
                color: "#fff !important",
              },
              "&& .MuiCircularProgress-root": {
                color: "#fff !important",
              },
              borderRadius: 2,
              py: 1,
              mb: 1.5,
            }}
          >
            {isFiltered
              ? fetchRequested
                ? "Fetch requested"
                : "Fetch & Download"
              : isVideoReady
                ? "View Video"
                : isFetching
                  ? "Fetching..."
                  : isCompressing
                    ? "Compressing..."
                    : video.status === VideoStatus.TRANSCRIBING
                      ? "Transcribing..."
                      : "Process Video Now"}
          </Button>

          <Typography
            variant="caption"
            display="block"
            align="center"
            sx={{ color: "text.secondary" }}
          >
            Uploaded:{" "}
            {new Date(video.uploadedDate).toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </Typography>
        </Box>
      </Card>
    </Grid>
  );
};
